import { z } from 'zod'
import { MemberIdSchema, OkResponseSchema, SlugSchema, UuidSchema } from './primitives'
import {
  AudiencePreviewQuerySchema,
  AudiencePreviewResponseSchema,
  CommunicationCreateRequestSchema,
  CommunicationListQuerySchema,
  CommunicationListResponseSchema,
  CommunicationResponseSchema,
  RecipientListQuerySchema,
  RecipientListResponseSchema,
  TestSendRequestSchema,
  TestSendResponseSchema,
  UnsubscribeInfoResponseSchema,
  UnsubscribeTokenSchema,
  UpdateEmailPreferencesRequestSchema,
} from './api/communications'
import {
  ContactCodeCheckResponseSchema,
  ContactCodeSentResponseSchema,
  EmailCodeCheckRequestSchema,
  EmailCodeSendRequestSchema,
  PhoneCodeCheckRequestSchema,
  PhoneCodeSendRequestSchema,
} from './api/contact'
import {
  CouncilConfirmRequestSchema,
  CouncilLinkRequestSchema,
  CouncilListResponseSchema,
  CouncilResponseSchema,
  CouncilSetupRequestSchema,
  CouncilSetupResponseSchema,
  CouncilUpdateRequestSchema,
  PairingCodeParamSchema,
  PairingCodeRequestSchema,
  PairingCodeResponseSchema,
  PairingCompleteRequestSchema,
  PairingCompleteResponseSchema,
  PairingResolveResponseSchema,
} from './api/councils'
import {
  GeoBoundaryQuerySchema,
  GeoBoundaryResponseSchema,
  GeoSearchQuerySchema,
  GeoSearchResponseSchema,
} from './api/geo'
import {
  ElectionCreateRequestSchema,
  ElectionDashboardResponseSchema,
  ElectionListResponseSchema,
  ElectionResponseSchema,
  ElectionUpdateRequestSchema,
  EndElectionRequestSchema,
  ExtendElectionRequestSchema,
  ResultsResponseSchema,
} from './api/elections'
import {
  CreateAccountRequestSchema,
  CspAttestationResponseSchema,
  CspRequestSchema,
  EraseMyDataRequestSchema,
  JoinRequestSchema,
  JoinResponseSchema,
  KycRetryResponseSchema,
  MemberElectionStatusResponseSchema,
  MemberLoginRequestSchema,
  MemberMeResponseSchema,
  MyElectionsResponseSchema,
  RecordVoteRequestSchema,
  RecordVoteResponseSchema,
  RecoverChallengeRequestSchema,
  RecoverChallengeResponseSchema,
  RecoverCompleteRequestSchema,
  RegistrationStatusResponseSchema,
  UpdateLocalityRequestSchema,
  VotesResponseSchema,
} from './api/member'
import {
  LiveDecisionResponseSchema,
  MemberDetailResponseSchema,
  MemberListQuerySchema,
  MemberListResponseSchema,
  MemberMapQuerySchema,
  MemberMapResponseSchema,
  ReadmitMemberRequestSchema,
  RemoveMemberRequestSchema,
  ResetMemberRequestSchema,
  ReviewQueueQuerySchema,
  ReviewRequestSchema,
  SetDistrictRequestSchema,
} from './api/members'
import { OrgLoginRequestSchema, OrgMeResponseSchema, OrgSignupRequestSchema } from './api/organizer'
import {
  OrganizationCreateRequestSchema,
  OrganizationDashboardResponseSchema,
  OrganizationListResponseSchema,
  OrganizationResponseSchema,
  OrganizationUpdateRequestSchema,
  RequirementsChangePreviewRequestSchema,
  RequirementsChangePreviewResponseSchema,
  SlugAvailabilityQuerySchema,
  SlugAvailabilityResponseSchema,
} from './api/organizations'
import {
  HealthResponseSchema,
  PublicConfigSchema,
  PublicElectionResponseSchema,
  PublicOrganizationResponseSchema,
} from './api/public'

/**
 * Typed route table: the ONE place that binds method + path + schemas. The web API client
 * (`apps/web/src/api/client.ts`) and the API routers both use it, so a contract change is a type error
 * on both sides.
 *
 * Path params use Express syntax (`:orgId`). `auth` documents the guard the server applies:
 * - `organizer`: `org_session` cookie; `:orgId` must belong to the organizer (else 404)
 * - `member`: `member_session` cookie for the organization in `:orgSlug`, member still active
 * - `registration`: `x-registration-token` header for the organization in `:orgSlug`
 *
 * Vocabulary: a "voting" in the UI is an `election` here (and in the database).
 */
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
export type RouteAuth = 'public' | 'organizer' | 'member' | 'registration'

export interface RouteDef<
  M extends HttpMethod = HttpMethod,
  P extends string = string,
  Params extends z.ZodType | undefined = z.ZodType | undefined,
  Query extends z.ZodType | undefined = z.ZodType | undefined,
  Body extends z.ZodType | undefined = z.ZodType | undefined,
  Res extends z.ZodType = z.ZodType,
> {
  method: M
  path: P
  auth: RouteAuth
  params: Params
  query: Query
  body: Body
  response: Res
}

const route = <
  M extends HttpMethod,
  P extends string,
  Res extends z.ZodType,
  Params extends z.ZodType | undefined = undefined,
  Query extends z.ZodType | undefined = undefined,
  Body extends z.ZodType | undefined = undefined,
>(def: {
  method: M
  path: P
  auth: RouteAuth
  response: Res
  params?: Params
  query?: Query
  body?: Body
}): RouteDef<M, P, Params, Query, Body, Res> => ({
  method: def.method,
  path: def.path,
  auth: def.auth,
  response: def.response,
  params: def.params as Params,
  query: def.query as Query,
  body: def.body as Body,
})

const OrgParams = z.object({ orgId: UuidSchema })
const MemberParams = OrgParams.extend({ memberId: MemberIdSchema })
const ElectionParams = OrgParams.extend({ electionId: UuidSchema })
const CommunicationParams = OrgParams.extend({ communicationId: UuidSchema })
const CouncilParams = OrgParams.extend({ councilId: UuidSchema })
const CouncilSetupParams = OrgParams.extend({ setupId: UuidSchema })
const PairingParams = z.object({ code: PairingCodeParamSchema })
const OrgSlugParams = z.object({ orgSlug: SlugSchema })
const ElectionSlugParams = OrgSlugParams.extend({ electionSlug: SlugSchema })
const UnsubscribeParams = z.object({ token: UnsubscribeTokenSchema })

const ORG = '/api/org/organizations/:orgId'
const MEMBER = `${ORG}/members/:memberId`
const ELECTION = `${ORG}/elections/:electionId`
const PORTAL = '/api/o/:orgSlug'
const PORTAL_ELECTION = `${PORTAL}/elections/:electionSlug`

export const routes = {
  /* ================================================================ health / public */
  health: route({ method: 'GET', path: '/api/health', auth: 'public', response: HealthResponseSchema }),
  publicConfig: route({ method: 'GET', path: '/api/public/config', auth: 'public', response: PublicConfigSchema }),
  publicOrganization: route({
    method: 'GET',
    path: '/api/public/orgs/:orgSlug',
    auth: 'public',
    params: OrgSlugParams,
    response: PublicOrganizationResponseSchema,
  }),
  publicElection: route({
    method: 'GET',
    path: '/api/public/orgs/:orgSlug/elections/:electionSlug',
    auth: 'public',
    params: ElectionSlugParams,
    response: PublicElectionResponseSchema,
  }),
  /** Email opt-out page (`/unsubscribe#token=`). */
  unsubscribeInfo: route({
    method: 'GET',
    path: '/api/public/unsubscribe/:token',
    auth: 'public',
    params: UnsubscribeParams,
    response: UnsubscribeInfoResponseSchema,
  }),
  /** Confirm the opt-out (web page) — also the RFC 8058 one-click target of List-Unsubscribe-Post (form body ignored). */
  unsubscribe: route({
    method: 'POST',
    path: '/api/public/unsubscribe/:token',
    auth: 'public',
    params: UnsubscribeParams,
    response: UnsubscribeInfoResponseSchema,
  }),

  /**
   * Council app → pairing (docs/pairing-api.md). Public, rate-limited, CORS for the Council app origin only. The code
   * is the only credential: one use, 60 minutes, dies when the organization asks for a new one.
   */
  resolveCouncilPairing: route({
    method: 'GET',
    path: '/api/public/council-pairing/:code',
    auth: 'public',
    params: PairingParams,
    response: PairingResolveResponseSchema,
  }),
  completeCouncilPairing: route({
    method: 'POST',
    path: '/api/public/council-pairing/:code/complete',
    auth: 'public',
    params: PairingParams,
    body: PairingCompleteRequestSchema,
    response: PairingCompleteResponseSchema,
  }),

  /* ================================================================ organizer auth */
  orgSignup: route({
    method: 'POST',
    path: '/api/org/signup',
    auth: 'public',
    body: OrgSignupRequestSchema,
    response: OrgMeResponseSchema,
  }),
  orgLogin: route({
    method: 'POST',
    path: '/api/org/login',
    auth: 'public',
    body: OrgLoginRequestSchema,
    response: OrgMeResponseSchema,
  }),
  orgLogout: route({ method: 'POST', path: '/api/org/logout', auth: 'public', response: OkResponseSchema }),
  orgMe: route({ method: 'GET', path: '/api/org/me', auth: 'organizer', response: OrgMeResponseSchema }),

  /* ================================================================ organizations */
  listOrganizations: route({
    method: 'GET',
    path: '/api/org/organizations',
    auth: 'organizer',
    response: OrganizationListResponseSchema,
  }),
  createOrganization: route({
    method: 'POST',
    path: '/api/org/organizations',
    auth: 'organizer',
    body: OrganizationCreateRequestSchema,
    response: OrganizationResponseSchema,
  }),
  /** Mounted before `:orgId` routes. */
  checkOrganizationSlug: route({
    method: 'GET',
    path: '/api/org/organizations/slug-available',
    auth: 'organizer',
    query: SlugAvailabilityQuerySchema,
    response: SlugAvailabilityResponseSchema,
  }),
  getOrganization: route({
    method: 'GET',
    path: ORG,
    auth: 'organizer',
    params: OrgParams,
    response: OrganizationResponseSchema,
  }),
  updateOrganization: route({
    method: 'PATCH',
    path: ORG,
    auth: 'organizer',
    params: OrgParams,
    body: OrganizationUpdateRequestSchema,
    response: OrganizationResponseSchema,
  }),
  deleteOrganization: route({
    method: 'DELETE',
    path: ORG,
    auth: 'organizer',
    params: OrgParams,
    response: OkResponseSchema,
  }),
  publishOrganization: route({
    method: 'POST',
    path: `${ORG}/publish`,
    auth: 'organizer',
    params: OrgParams,
    response: OrganizationResponseSchema,
  }),
  organizationDashboard: route({
    method: 'GET',
    path: `${ORG}/dashboard`,
    auth: 'organizer',
    params: OrgParams,
    response: OrganizationDashboardResponseSchema,
  }),
  /** Impact of a requirements change on existing members (PLAN v4 R6). Nothing is saved. */
  previewRequirementsChange: route({
    method: 'POST',
    path: `${ORG}/requirements/preview`,
    auth: 'organizer',
    params: OrgParams,
    body: RequirementsChangePreviewRequestSchema,
    response: RequirementsChangePreviewResponseSchema,
  }),

  /* ================================================================ geocoding proxy (area editor, PLAN v4 §9) */
  /** Place search (Nominatim /search, no polygons). Explicit searches only: never call it per keystroke. */
  geoSearch: route({
    method: 'GET',
    path: '/api/org/geo/search',
    auth: 'organizer',
    query: GeoSearchQuerySchema,
    response: GeoSearchResponseSchema,
  }),
  /** Boundary of a picked candidate (Nominatim /lookup with polygon), simplified, as a storable `place` area. */
  geoBoundary: route({
    method: 'GET',
    path: '/api/org/geo/boundary',
    auth: 'organizer',
    query: GeoBoundaryQuerySchema,
    response: GeoBoundaryResponseSchema,
  }),

  memberMap: route({
    method: 'GET',
    path: `${ORG}/map`,
    auth: 'organizer',
    params: OrgParams,
    query: MemberMapQuerySchema,
    response: MemberMapResponseSchema,
  }),

  /* ================================================================ members (organizer) */
  listMembers: route({
    method: 'GET',
    path: `${ORG}/members`,
    auth: 'organizer',
    params: OrgParams,
    query: MemberListQuerySchema,
    response: MemberListResponseSchema,
  }),
  reviewQueue: route({
    method: 'GET',
    path: `${ORG}/review-queue`,
    auth: 'organizer',
    params: OrgParams,
    query: ReviewQueueQuerySchema,
    response: MemberListResponseSchema,
  }),
  getMember: route({
    method: 'GET',
    path: MEMBER,
    auth: 'organizer',
    params: MemberParams,
    response: MemberDetailResponseSchema,
  }),
  reviewMember: route({
    method: 'POST',
    path: `${MEMBER}/review`,
    auth: 'organizer',
    params: MemberParams,
    body: ReviewRequestSchema,
    response: MemberDetailResponseSchema,
  }),
  setMemberDistrict: route({
    method: 'PATCH',
    path: `${MEMBER}/district`,
    auth: 'organizer',
    params: MemberParams,
    body: SetDistrictRequestSchema,
    response: MemberDetailResponseSchema,
  }),
  removeMember: route({
    method: 'POST',
    path: `${MEMBER}/remove`,
    auth: 'organizer',
    params: MemberParams,
    body: RemoveMemberRequestSchema,
    response: MemberDetailResponseSchema,
  }),
  readmitMember: route({
    method: 'POST',
    path: `${MEMBER}/readmit`,
    auth: 'organizer',
    params: MemberParams,
    body: ReadmitMemberRequestSchema,
    response: MemberDetailResponseSchema,
  }),
  resetMember: route({
    method: 'POST',
    path: `${MEMBER}/reset`,
    auth: 'organizer',
    params: MemberParams,
    body: ResetMemberRequestSchema,
    response: MemberDetailResponseSchema,
  }),
  liveDecision: route({
    method: 'GET',
    path: `${MEMBER}/kyc/:sessionId/decision`,
    auth: 'organizer',
    params: MemberParams.extend({ sessionId: z.string().min(1) }),
    response: LiveDecisionResponseSchema,
  }),

  /* ================================================================ communications (organizer) */
  listCommunications: route({
    method: 'GET',
    path: `${ORG}/communications`,
    auth: 'organizer',
    params: OrgParams,
    query: CommunicationListQuerySchema,
    response: CommunicationListResponseSchema,
  }),
  createCommunication: route({
    method: 'POST',
    path: `${ORG}/communications`,
    auth: 'organizer',
    params: OrgParams,
    body: CommunicationCreateRequestSchema,
    response: CommunicationResponseSchema,
  }),
  /** Mounted before `:communicationId` routes. */
  previewAudience: route({
    method: 'GET',
    path: `${ORG}/communications/audience`,
    auth: 'organizer',
    params: OrgParams,
    query: AudiencePreviewQuerySchema,
    response: AudiencePreviewResponseSchema,
  }),
  sendTestCommunication: route({
    method: 'POST',
    path: `${ORG}/communications/test`,
    auth: 'organizer',
    params: OrgParams,
    body: TestSendRequestSchema,
    response: TestSendResponseSchema,
  }),
  getCommunication: route({
    method: 'GET',
    path: `${ORG}/communications/:communicationId`,
    auth: 'organizer',
    params: CommunicationParams,
    response: CommunicationResponseSchema,
  }),
  listRecipients: route({
    method: 'GET',
    path: `${ORG}/communications/:communicationId/recipients`,
    auth: 'organizer',
    params: CommunicationParams,
    query: RecipientListQuerySchema,
    response: RecipientListResponseSchema,
  }),

  /* ================================================================ election committees (organizer) */
  listCouncils: route({
    method: 'GET',
    path: `${ORG}/councils`,
    auth: 'organizer',
    params: OrgParams,
    response: CouncilListResponseSchema,
  }),
  /** Paste path (expert fallback): the committee then needs the fingerprint confirmation. */
  linkCouncil: route({
    method: 'POST',
    path: `${ORG}/councils`,
    auth: 'organizer',
    params: OrgParams,
    body: CouncilLinkRequestSchema,
    response: CouncilResponseSchema,
  }),
  /** Mounted before `:councilId` routes. A fresh one-use pairing code (60 min). */
  createCouncilPairing: route({
    method: 'POST',
    path: `${ORG}/councils/pairing`,
    auth: 'organizer',
    params: OrgParams,
    body: PairingCodeRequestSchema,
    response: PairingCodeResponseSchema,
  }),
  /** Mounted before `:councilId` routes. "Create a committee": the committee shows as being set up from now on. */
  createCouncilSetup: route({
    method: 'POST',
    path: `${ORG}/councils/setups`,
    auth: 'organizer',
    params: OrgParams,
    body: CouncilSetupRequestSchema,
    response: CouncilSetupResponseSchema,
  }),
  /** Remove a committee being set up (abandoned, or started by mistake). Nothing happens in the Council app. */
  dismissCouncilSetup: route({
    method: 'DELETE',
    path: `${ORG}/councils/setups/:setupId`,
    auth: 'organizer',
    params: CouncilSetupParams,
    response: OkResponseSchema,
  }),
  updateCouncil: route({
    method: 'PATCH',
    path: `${ORG}/councils/:councilId`,
    auth: 'organizer',
    params: CouncilParams,
    body: CouncilUpdateRequestSchema,
    response: CouncilResponseSchema,
  }),
  confirmCouncilFingerprint: route({
    method: 'POST',
    path: `${ORG}/councils/:councilId/confirm-fingerprint`,
    auth: 'organizer',
    params: CouncilParams,
    body: CouncilConfirmRequestSchema,
    response: CouncilResponseSchema,
  }),
  /** Re-read the committee on chain now (the sync worker also does it every tick). */
  refreshCouncil: route({
    method: 'POST',
    path: `${ORG}/councils/:councilId/refresh`,
    auth: 'organizer',
    params: CouncilParams,
    response: CouncilResponseSchema,
  }),
  /** Unlink (nothing happens on chain). 409 while a non-draft voting uses the committee. */
  unlinkCouncil: route({
    method: 'DELETE',
    path: `${ORG}/councils/:councilId`,
    auth: 'organizer',
    params: CouncilParams,
    response: OkResponseSchema,
  }),

  /* ================================================================ votings (organizer) */
  listElections: route({
    method: 'GET',
    path: `${ORG}/elections`,
    auth: 'organizer',
    params: OrgParams,
    response: ElectionListResponseSchema,
  }),
  createElection: route({
    method: 'POST',
    path: `${ORG}/elections`,
    auth: 'organizer',
    params: OrgParams,
    body: ElectionCreateRequestSchema,
    response: ElectionResponseSchema,
  }),
  getElection: route({
    method: 'GET',
    path: ELECTION,
    auth: 'organizer',
    params: ElectionParams,
    response: ElectionResponseSchema,
  }),
  updateElection: route({
    method: 'PATCH',
    path: ELECTION,
    auth: 'organizer',
    params: ElectionParams,
    body: ElectionUpdateRequestSchema,
    response: ElectionResponseSchema,
  }),
  deleteElection: route({
    method: 'DELETE',
    path: ELECTION,
    auth: 'organizer',
    params: ElectionParams,
    response: OkResponseSchema,
  }),
  publishElection: route({
    method: 'POST',
    path: `${ELECTION}/publish`,
    auth: 'organizer',
    params: ElectionParams,
    response: ElectionResponseSchema,
  }),
  startElection: route({
    method: 'POST',
    path: `${ELECTION}/start`,
    auth: 'organizer',
    params: ElectionParams,
    response: ElectionResponseSchema,
  }),
  retryStartElection: route({
    method: 'POST',
    path: `${ELECTION}/retry`,
    auth: 'organizer',
    params: ElectionParams,
    response: ElectionResponseSchema,
  }),
  endElection: route({
    method: 'POST',
    path: `${ELECTION}/end`,
    auth: 'organizer',
    params: ElectionParams,
    body: EndElectionRequestSchema,
    response: ElectionResponseSchema,
  }),
  extendElection: route({
    method: 'POST',
    path: `${ELECTION}/extend`,
    auth: 'organizer',
    params: ElectionParams,
    body: ExtendElectionRequestSchema,
    response: ElectionResponseSchema,
  }),
  cancelElection: route({
    method: 'POST',
    path: `${ELECTION}/cancel`,
    auth: 'organizer',
    params: ElectionParams,
    response: ElectionResponseSchema,
  }),
  electionDashboard: route({
    method: 'GET',
    path: `${ELECTION}/dashboard`,
    auth: 'organizer',
    params: ElectionParams,
    response: ElectionDashboardResponseSchema,
  }),
  electionResults: route({
    method: 'GET',
    path: `${ELECTION}/results`,
    auth: 'organizer',
    params: ElectionParams,
    response: ResultsResponseSchema,
  }),

  /* ================================================================ member portal: contact codes, join, KYC */
  sendEmailCode: route({
    method: 'POST',
    path: `${PORTAL}/contact/email/send`,
    auth: 'public',
    params: OrgSlugParams,
    body: EmailCodeSendRequestSchema,
    response: ContactCodeSentResponseSchema,
  }),
  checkEmailCode: route({
    method: 'POST',
    path: `${PORTAL}/contact/email/check`,
    auth: 'public',
    params: OrgSlugParams,
    body: EmailCodeCheckRequestSchema,
    response: ContactCodeCheckResponseSchema,
  }),
  sendPhoneCode: route({
    method: 'POST',
    path: `${PORTAL}/contact/phone/send`,
    auth: 'public',
    params: OrgSlugParams,
    body: PhoneCodeSendRequestSchema,
    response: ContactCodeSentResponseSchema,
  }),
  checkPhoneCode: route({
    method: 'POST',
    path: `${PORTAL}/contact/phone/check`,
    auth: 'public',
    params: OrgSlugParams,
    body: PhoneCodeCheckRequestSchema,
    response: ContactCodeCheckResponseSchema,
  }),
  join: route({
    method: 'POST',
    path: `${PORTAL}/join`,
    auth: 'public',
    params: OrgSlugParams,
    body: JoinRequestSchema,
    response: JoinResponseSchema,
  }),
  registrationStatus: route({
    method: 'GET',
    path: `${PORTAL}/registration`,
    auth: 'registration',
    params: OrgSlugParams,
    response: RegistrationStatusResponseSchema,
  }),
  retryKyc: route({
    method: 'POST',
    path: `${PORTAL}/kyc/session`,
    auth: 'registration',
    params: OrgSlugParams,
    response: KycRetryResponseSchema,
  }),

  /* ================================================================ member portal: account */
  createAccount: route({
    method: 'POST',
    path: `${PORTAL}/account`,
    auth: 'registration',
    params: OrgSlugParams,
    body: CreateAccountRequestSchema,
    response: MemberMeResponseSchema,
  }),
  memberLogin: route({
    method: 'POST',
    path: `${PORTAL}/login`,
    auth: 'public',
    params: OrgSlugParams,
    body: MemberLoginRequestSchema,
    response: MemberMeResponseSchema,
  }),
  memberLogout: route({
    method: 'POST',
    path: `${PORTAL}/logout`,
    auth: 'public',
    params: OrgSlugParams,
    response: OkResponseSchema,
  }),
  memberMe: route({
    method: 'GET',
    path: `${PORTAL}/me`,
    auth: 'member',
    params: OrgSlugParams,
    response: MemberMeResponseSchema,
  }),
  updateLocality: route({
    method: 'PATCH',
    path: `${PORTAL}/me/locality`,
    auth: 'member',
    params: OrgSlugParams,
    body: UpdateLocalityRequestSchema,
    response: MemberMeResponseSchema,
  }),
  updateEmailPreferences: route({
    method: 'PATCH',
    path: `${PORTAL}/me/email`,
    auth: 'member',
    params: OrgSlugParams,
    body: UpdateEmailPreferencesRequestSchema,
    response: MemberMeResponseSchema,
  }),
  eraseMyData: route({
    method: 'POST',
    path: `${PORTAL}/me/erase`,
    auth: 'member',
    params: OrgSlugParams,
    body: EraseMyDataRequestSchema,
    response: OkResponseSchema,
  }),
  myElections: route({
    method: 'GET',
    path: `${PORTAL}/me/elections`,
    auth: 'member',
    params: OrgSlugParams,
    response: MyElectionsResponseSchema,
  }),
  recoverChallenge: route({
    method: 'POST',
    path: `${PORTAL}/recover/challenge`,
    auth: 'public',
    params: OrgSlugParams,
    body: RecoverChallengeRequestSchema,
    response: RecoverChallengeResponseSchema,
  }),
  recoverComplete: route({
    method: 'POST',
    path: `${PORTAL}/recover/complete`,
    auth: 'public',
    params: OrgSlugParams,
    body: RecoverCompleteRequestSchema,
    response: MemberMeResponseSchema,
  }),

  /* ================================================================ member portal: voting */
  memberElectionStatus: route({
    method: 'GET',
    path: `${PORTAL_ELECTION}/me`,
    auth: 'member',
    params: ElectionSlugParams,
    response: MemberElectionStatusResponseSchema,
  }),
  csp: route({
    method: 'POST',
    path: `${PORTAL_ELECTION}/csp`,
    auth: 'member',
    params: ElectionSlugParams,
    body: CspRequestSchema,
    response: CspAttestationResponseSchema,
  }),
  recordVote: route({
    method: 'POST',
    path: `${PORTAL_ELECTION}/votes`,
    auth: 'member',
    params: ElectionSlugParams,
    body: RecordVoteRequestSchema,
    response: RecordVoteResponseSchema,
  }),
  listVotes: route({
    method: 'GET',
    path: `${PORTAL_ELECTION}/votes`,
    auth: 'member',
    params: ElectionSlugParams,
    response: VotesResponseSchema,
  }),
} as const

export type Routes = typeof routes
export type RouteName = keyof Routes

/** Infer helpers: `RouteResponse<'getElection'>`, `RouteBody<'createElection'>` (input shape). */
export type RouteResponse<N extends RouteName> = z.infer<Routes[N]['response']>
export type RouteBody<N extends RouteName> = Routes[N]['body'] extends z.ZodType ? z.input<Routes[N]['body']> : never
export type RouteQuery<N extends RouteName> = Routes[N]['query'] extends z.ZodType ? z.input<Routes[N]['query']> : never
export type RouteParams<N extends RouteName> = Routes[N]['params'] extends z.ZodType
  ? z.input<Routes[N]['params']>
  : never

/** Fill `:param` placeholders, URL-encoding values. */
export function buildPath(path: string, params: Record<string, string | number> = {}): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, key: string) => {
    const v = params[key]
    if (v === undefined) throw new Error(`Missing path param "${key}" for ${path}`)
    return encodeURIComponent(String(v))
  })
}

/** Paths that are not JSON contracts (documented here so nobody re-invents them). */
export const RAW_PATHS = {
  /** Sequencer reverse proxy (allow-listed SDK paths only). Browser SDK: `sequencerUrls: [origin + SEQUENCER_PROXY_PATH]`. */
  sequencerProxy: '/api/seq',
  /** Byte-exact process metadata documents (CORS *). */
  document: '/api/documents/:sha256.json',
  /** Didit webhook (HMAC verified). */
  diditWebhook: '/api/webhooks/didit',
} as const
export const SEQUENCER_PROXY_PATH = RAW_PATHS.sequencerProxy

/**
 * SPA paths the SERVER puts in links (Didit callback, emails, public URLs) — the web router must serve them.
 * Prefix with PUBLIC_URL server-side.
 */
export const WEB_PATHS = {
  orgLanding: (orgSlug: string) => `/o/${orgSlug}`,
  orgJoin: (orgSlug: string) => `/o/${orgSlug}/join`,
  /** Didit `callback` after the hosted verification. */
  verifyReturn: (orgSlug: string) => `/o/${orgSlug}/join/return`,
  /**
   * Registration status. With `resumeToken` (review-decision email): the token rides in the fragment (`#rt=…`, never
   * sent to a server nor in a Referer); the page stores it as the registration token of this browser.
   */
  registrationStatus: (orgSlug: string, resumeToken?: string) =>
    `/o/${orgSlug}/status${resumeToken ? `#rt=${encodeURIComponent(resumeToken)}` : ''}`,
  memberLogin: (orgSlug: string) => `/o/${orgSlug}/login`,
  /** Privacy notice: generic, or with an organization's own choices (face template retention). */
  privacy: (orgSlug?: string) => (orgSlug ? `/o/${orgSlug}/privacy` : '/privacy'),
  /** Member profile (email preferences, "Delete my data"). */
  memberProfile: (orgSlug: string) => `/o/${orgSlug}/profile`,
  memberHome: (orgSlug: string) => `/o/${orgSlug}/home`,
  /** Voting page; invitation links deep-link here (login first, then back). */
  voting: (orgSlug: string, electionSlug: string) => `/o/${orgSlug}/v/${electionSlug}`,
  /** Email footer opt-out page. */
  /** Fragment, not query: the token never reaches a server (no access log carries it). */
  unsubscribe: (token: string) => `/unsubscribe#token=${encodeURIComponent(token)}`,
  /**
   * Organizer: the organization's election committees tab. Also the Council app's "back to DAVINCI Elections" link
   * after a pairing (`PairingCompleteResponse.returnPath`; the Council app allowlists origin + this path shape).
   */
  orgCommittees: (orgId: string) => `/organizer/orgs/${orgId}/committees`,
  /** Organizer: a voting's results tab (organizer notices). */
  votingResults: (orgId: string, electionId: string) => `/organizer/orgs/${orgId}/votings/${electionId}/results`,
} as const
